import { Component, Injector, Input, OnChanges, OnInit, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import {
  AbstractComponent,
  Channel,
  ChannelHealthService,
  ChannelMetaUsageResponse,
} from 'lib-trend-core';
import { takeUntil } from 'rxjs';

/** Meta categories with their own label; any other one is shown by Meta's identifier. */
const CATEGORY_LABEL_KEYS: Record<string, string> = {
  SERVICE: 'report.category.service',
  MARKETING: 'report.category.marketing',
  MARKETING_LITE: 'channel.health.metaUsage.categoryMarketingLite',
  UTILITY: 'report.category.utility',
  AUTHENTICATION: 'report.category.authentication',
  AUTHENTICATION_INTERNATIONAL: 'channel.health.metaUsage.categoryAuthenticationInternational',
};

export interface MetaUsageAlert {
  cssClass: string;
  icon: string;
  text: string;
}

/**
 * "Resumo" card — Meta's monthly allowance of free service messages for this channel's number
 * (charged from the first message beyond it) and what Meta charged the number this month so far,
 * in total and per category. It loads on its own, so a slow or failing Meta read never holds the
 * rest of the tab; when Meta cannot be read the API answers the platform's estimate instead.
 */
@Component({
    selector: 'health-meta-usage-card',
    templateUrl: './health-meta-usage-card.component.html',
    styleUrl: './health-meta-usage-card.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HealthMetaUsageCardComponent extends AbstractComponent implements OnInit, OnChanges {

  @Input({ required: true }) channel: Channel;
  @Input() refreshTick: number = 0;

  usage?: ChannelMetaUsageResponse;
  failed: boolean = false;

  constructor(
    public injector: Injector,
    private channelHealthService: ChannelHealthService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['refreshTick'] && !changes['refreshTick'].firstChange) {
      this.load();
    }
  }

  load(): void {
    this.loadingContent = true;
    this.failed = false;
    this.channelHealthService.getMetaUsage(this.channel._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (usage) => {
          this.usage = usage;
          this.loadingContent = false;
        },
        // Inline state with a retry — the tab's own endpoints already toast their errors.
        error: () => {
          this.failed = true;
          this.loadingContent = false;
        },
      });
  }

  // ---------------------------------------------------------------- allowance meter

  usedLabel(): string {
    const allowance = this.usage?.serviceAllowance;
    return this.t('channel.health.metaUsage.used', {
      used: this.formatNumber(allowance?.used),
      limit: this.formatNumber(allowance?.limit),
    });
  }

  remainingLabel(): string {
    return this.t('channel.health.metaUsage.remaining', { count: this.formatNumber(this.usage?.serviceAllowance?.remaining) });
  }

  percentLabel(): string {
    return `${Math.round((this.usage?.serviceAllowance?.usageRatio ?? 0) * 100)}%`;
  }

  /** Bar width, capped at 100% once the allowance is exhausted. */
  progressWidth(): number {
    return Math.min(100, Math.max(0, (this.usage?.serviceAllowance?.usageRatio ?? 0) * 100));
  }

  progressBarClass(): string {
    const map: Record<string, string> = { OK: 'bg-success', WARNING: 'bg-warning', EXHAUSTED: 'bg-danger' };
    return map[this.usage?.serviceAllowance?.status ?? 'OK'] ?? 'bg-success';
  }

  subtitle(): string {
    return this.t('channel.health.metaUsage.subtitle', {
      start: this.formatDate(this.usage?.period?.start),
      reset: this.formatDate(this.usage?.period?.resetsAt),
    });
  }

  /** Warns from 80% of the allowance and once every free message was used. */
  statusAlert(): MetaUsageAlert | null {
    const usage = this.usage;
    if (!usage) return null;
    if (usage.serviceAllowance.status === 'EXHAUSTED') {
      return {
        cssClass: 'alert-danger',
        icon: 'ri-error-warning-line',
        text: this.t('channel.health.metaUsage.alertExhausted', { date: this.formatDate(usage.period.resetsAt) }),
      };
    }
    if (usage.serviceAllowance.status === 'WARNING') {
      return {
        cssClass: 'alert-warning',
        icon: 'ri-alert-line',
        text: this.t('channel.health.metaUsage.alertWarning', {
          percent: this.percentLabel(),
          next: this.formatNumber(usage.serviceAllowance.limit + 1),
        }),
      };
    }
    return null;
  }

  sourceLabel(): string {
    return this.t(this.usage?.source === 'PLATFORM' ? 'channel.health.metaUsage.sourcePlatform' : 'channel.health.metaUsage.sourceMeta');
  }

  /** System status-badge variants (`.campaign-badge` — the shared standard). */
  sourceBadgeClass(): string {
    return this.usage?.source === 'PLATFORM' ? 'is-warn' : 'is-neutral';
  }

  /** Service messages Meta charged; the platform estimate can only infer them from the count. */
  billedLabel(): string {
    const usage = this.usage;
    if (!usage) return '—';
    const volume = usage.service.billableVolume ?? usage.serviceAllowance.exceeded;
    const cost = usage.service.cost;
    return cost === null || cost === undefined
      ? this.formatNumber(volume)
      : `${this.formatNumber(volume)} · ${this.formatCost(cost)}`;
  }

  // ---------------------------------------------------------------- month spend

  /** Meta's spend table — only when Meta told it; otherwise the client-rate estimate takes its place. */
  showMonthSpend(): boolean {
    return this.usage?.source === 'META' && !!this.usage?.monthTotal && !this.usage?.serviceEstimate;
  }

  // ---------------------------------------------------------------- service estimate (no Meta spend)

  /** Meta told no spend (cost refused or Meta unread): the service messages priced at the client's rate. */
  showServiceEstimate(): boolean {
    return !!this.usage?.serviceEstimate;
  }

  /** The right-hand column is there when either the Meta spend or the estimate is. */
  showSideColumn(): boolean {
    return this.showMonthSpend() || this.showServiceEstimate();
  }

  /** "5.924 × R$ 0,035" */
  paidFormulaLabel(): string {
    const estimate = this.usage?.serviceEstimate;
    if (!estimate) return '';
    return `${this.formatNumber(estimate.paid)} × ${this.formatBrl(estimate.unitPrice)}`;
  }

  /** Price per message keeps every decimal the contract has (0,035); totals keep cents. */
  formatBrl(value: number | null | undefined): string {
    if (value === null || value === undefined) return '—';
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: Math.abs(value) >= 1 || value === 0 ? 2 : 4,
    });
  }

  /** Everything Meta charged this number since day 1 of the month, every category summed. */
  monthTotalCostLabel(): string {
    return this.formatCost(this.usage?.monthTotal?.cost);
  }

  /** Messages went out but Meta reports no cost — the account is billed through a partner's credit line. */
  costUnavailable(): boolean {
    const total = this.usage?.monthTotal;
    return !!total && total.cost === null && total.volume > 0;
  }

  categoryLabel(category: string): string {
    const key = CATEGORY_LABEL_KEYS[category];
    return key ? this.t(key) : category;
  }

  // ---------------------------------------------------------------- formatting

  formatNumber(value: number | null | undefined): string {
    if (value === null || value === undefined) return '—';
    return value.toLocaleString('pt-BR');
  }

  /** Amounts from 1 up keep cents; smaller ones keep 4 decimals — one message costs fractions of a cent. */
  formatCost(value: number | null | undefined): string {
    if (value === null || value === undefined) return '—';
    const currency = this.usage?.currency || 'USD';
    try {
      return value.toLocaleString('pt-BR', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: Math.abs(value) >= 1 ? 2 : 4,
      });
    } catch {
      return `${currency} ${value.toFixed(2)}`;
    }
  }

  /** dd/MM/yyyy in the allowance's own calendar (São Paulo) — the browser zone may differ. */
  formatDate(value: Date | string | null | undefined): string {
    if (!value) return '—';
    const date = new Date(value);
    try {
      return new Intl.DateTimeFormat('pt-BR', {
        timeZone: this.usage?.period?.timeZone,
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(date);
    } catch {
      return date.toLocaleDateString('pt-BR');
    }
  }
}
